import type { AxiosRequestConfig } from 'axios';

import axios from 'axios';



// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL ?? '',
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Optional: Add token (if using auth)
 *
 axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
*
*/

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error?.response?.data?.message || error?.message || 'Something went wrong!';
    console.error('Axios error:', message);
    return Promise.reject(new Error(message));
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async <T = unknown>(
  args: string | [string, AxiosRequestConfig]
): Promise<T> => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args, {}];

    const res = await axiosInstance.get<T>(url, config);

    return res.data;
  } catch (error) {
    console.error('Fetcher failed:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  auth: {
    me: '/auth/me',
    signIn: '/auth/sign-in',
    signUp: '/auth/sign-up',
  },
  categories: {
    /** GET /api/v1/categories — root formation categories with subcategories */
    list: '/v1/categories',
  },
  typeActualites: {
    /** GET /api/v1/type-actualites — all active news types (for tab navigation) */
    list: '/v1/type-actualites',
  },
  formations: {
    /** GET /api/v1/formations — paginated formations catalog */
    list: '/v1/formations',
    /** GET /api/v1/formations/:slug — single formation detail */
    details: (slug: string) => `/v1/formations/${slug}`,
    /** GET /api/v1/formations/:slug/recommended — other formations in the same category */
    recommended: (slug: string) => `/v1/formations/${slug}/recommended`,
    /** GET /api/v1/formations/:slug/programme — download the program PDF as an attachment */
    programme: (slug: string) => `/v1/formations/${slug}/programme`,
    /** POST /api/v1/formations/:slug/share — email this formation's program to someone */
    share: (slug: string) => `/v1/formations/${slug}/share`,
  },
  actualites: {
    /** GET /api/v1/actualites[?type=slug] — paginated news, optionally filtered by news type */
    list: '/v1/actualites',
    /** GET /api/v1/actualites/:categorySlug — latest 3 news for a formation category (legacy) */
    byCategory: (categorySlug: string) => `/v1/actualites/${categorySlug}`,
    /** GET /api/v1/actualites/:typeSlug/:slug — single article with full sections */
    detail: (typeSlug: string, slug: string) => `/v1/actualites/${typeSlug}/${slug}`,
    // Backward-compat alias — points to the same paginated endpoint
    latest: '/v1/actualites',
  },
  leads: {
    /** POST /api/v1/leads — create a lead from a public form (contact page, homepage popup, etc.) */
    create: '/v1/leads',
  },
  quiz: {
    /** POST /api/v1/quiz — submit the orientation quiz (creates/updates a lead + stores answers) */
    submit: '/v1/quiz',
  },
  mentors: {
    /** GET /api/v1/mentors — active trainers shown on the "Enseigner" page */
    list: '/v1/mentors',
  },
  financings: {
    /** GET /api/v1/financings — active financing options shown on the "Financements" carousel */
    list: '/v1/financings',
  },
  reviews: {
    /** GET /api/v1/reviews[?page=N] — approved reviews across all formations (paginated) */
    list: (page?: number): string | [string, AxiosRequestConfig] =>
      page ? ['/v1/reviews', { params: { page } }] : '/v1/reviews',
    /** POST /api/v1/formations/:slug/reviews — submit a review (created pending, then moderated) */
    create: (slug: string) => `/v1/formations/${slug}/reviews`,
  },
  testimonials: {
    /** GET /api/v1/testimonials — published testimonials shown on the homepage & Avis page */
    list: '/v1/testimonials',
  },
};
